import * as React from 'react'

import { create } from '@bufbuild/protobuf'
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Translation } from 'react-i18next'

import { useDisclosure } from '@chakra-ui/react'

import { ColumnHeader, DataTable } from '@enechain/ecloud-designsystem'

import { OrganizationAdmin } from '~/components/authorize/OrganizationAdmin'
import { Member, MemberSchema } from '~/gen/proto/bff/v1/model_pb'
import MembersTableRow, {
  MemberRow,
  memberTableColumns,
} from '~/pages/Members/components/MembersTable/MembersTableRow'
import ReSendInvtationModal from '~/pages/Members/components/MembersTable/ReSendInvitationModal'

type Props = {
  members: Member[]
}

const MembersTable: React.FC<Props> = ({ members }) => {
  const [resendReceiver, setResendReceiver] = React.useState<Member>(
    create(MemberSchema),
  )
  const {
    open: isResendOpen,
    onOpen: onResendOpen,
    onClose: onResendClose,
  } = useDisclosure()

  // NOTE: `data` must keep a stable reference between renders — depend only
  // on stable values here. An unstable data reference makes useReactTable
  // re-render endlessly (infinite loop) once DataTable is rendered without
  // memoization.
  const data = React.useMemo(() => {
    return members.map((v) => {
      return new MemberRow(
        v,
        (
          <OrganizationAdmin>
            <MembersTableRow
              member={v}
              onClickResendInvitation={() => {
                setResendReceiver(v)
                onResendOpen()
              }}
            />
          </OrganizationAdmin>
        ),
      )
    })
  }, [members, onResendOpen])

  const table = useReactTable({
    columns: memberTableColumns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })
  // NOTE: do not wrap DataTable in useMemo keyed by `table` — useReactTable
  // returns a stable instance whose internal state changes, so a memo keyed
  // by the instance would keep rendering stale rows when `members` changes
  // (e.g. when the member filter is applied).
  return (
    <>
      <DataTable
        renderHeader={(header): JSX.Element => {
          return (
            <Translation key={header.column.id}>
              {(t) => (
                <ColumnHeader>
                  {header.column.id === 'rest'
                    ? ''
                    : t(`pages.members.label.${header.column.id}`)}
                </ColumnHeader>
              )}
            </Translation>
          )
        }}
        table={table}
      />
      <ReSendInvtationModal
        open={isResendOpen}
        member={resendReceiver}
        onClose={onResendClose}
      />
    </>
  )
}

export default React.memo(MembersTable)
